<template>
  <div class="access-event-card__match-container">
    <img
      :src="imageUrl"
      :class="{confirmed: verificationClass.green, misuse: verificationClass.yellow, unverified: verificationClass.gray, cotenant: verificationClass.cotenant}"
      class='access-event-card__avatar'
      :title="confidenceTitle"
    />
    <!-- Bottom Info Box -->
    <div
      class="access-event-card__tag"
      :class='{verified: isVerified, unverified: !isVerified, cotenant: isCotenant && isVerified}'
      >
      <!-- Circle Confidence Indicator -->
      <div
        v-if='bestMatchSynopsis'
        class='access-event-card__match-confidence'
        :class='verificationClass'
        />
      <div
        v-if='bestMatchSynopsis'
        class='access-event-card__best-match-synopsis'
        >
        {{ bestMatchSynopsis }} Confidence {{ isCotenant ? "Cotenant" : ""}} AI Match
      </div>
      <!-- Verified By Text -->
      <div
        class='tag-text'
        v-if="showVerified && verified.by"
        >
          Verified by {{ verified.by }}, {{ verified.at }}
      </div>
      <div
        class='tag-text'
        v-else-if='(showVerified && verified.at) || isVerified'
        >
          Human Verified
      </div>
      <div
        class='tag-text'
        v-else
        >
          Not Verified
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccessEventMatch',
  props: {
    detectionId: {
      type: String,
    },
    detection: {
      type: Object,
    },
    bestMatchSynopsis: {
      type: String,
    },
    showVerified: {
      type: Boolean,
      default: true,
    },
    verified: {
      type: Object,
      default: undefined,
    },
    isCotenant: {
      type: Boolean,
    },
    imageUrl: {
      type: String,
    },
  },
  computed: {
    confidenceTitle () {
      if (!this.detection || !this.detection.matchConfidences) {
        return '?'
      }
      const mc = this.detection.matchConfidences
      if (mc.euc && mc.cos) {
        return 'euc: ' + (mc.euc.toFixed(4)) + ' cos: ' + (mc.cos.toFixed(4))
      } else if (mc.svn_margin) {
        return 'svn: ' + mc.svn_margin
      } else if (mc.nn_prob) {
        return 'nn: ' + mc.nn_prob
      } else if (mc.face_detector_conf) {
        return `fdc: ${mc.face_detector_conf.toFixed(4)}`
      }
      return '?'
    },
    /** @returns {Boolean} `true` if there is either a verified.by or verified.at, else `false`. */
    isVerified () {
      // for some reason this is returning the string at this.verified.by
      // https://stackoverflow.com/questions/31155477/cleanest-way-to-convert-to-boolean
      return Boolean((this.verified && (this.verified.by || this.verified.at)) || this.detection.score > 2)
    },
    /** @returns {Object} An object containing the appropriate CSS class bindings. */
    verificationClass () {
      const bestMatchSynopsis = this.bestMatchSynopsis
      const verified = (this.isVerified && !this.detection.isAbsent)
      const highConfidence = (bestMatchSynopsis === 'High' || bestMatchSynopsis === 'Medium' || verified) && !this.isCotenant
      const otherConfidence = (bestMatchSynopsis === 'Low') && !highConfidence
      const undefinedConfidence = (bestMatchSynopsis === undefined || bestMatchSynopsis === null) && !(otherConfidence || this.isVerified)
      const classes = { green: highConfidence, yellow: otherConfidence, gray: undefinedConfidence, cotenant: this.isCotenant }
      return classes
    },
  },
}
</script>

<style scoped>
.access-event-card__match-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;
  flex-shrink: 0;
  color: #324057;
}

/** The avatar in the match container. */
.access-event-card__avatar {
  background-color: #eff2f7;
  object-fit: cover;
  height: 6.875rem;
  width: 6.875rem;
  border-radius: 50%;
  margin: 0.33rem 1.06rem 0.33rem auto;
}

/** The border on avatars that are confirmed or suspected misuse. */
.confirmed {
  border: 5px solid rgba(34, 139, 34, 0.5);
}

.misuse {
  border: 5px solid yellow;
}

.unverified {
  border: 5px solid gray;
}

.cotenant {
  border: 5px solid rgba(139,34,139, .5);
}

/** The tag beneath the avatar. */
.access-event-card__tag {
  display: flex;
  align-items: center;
  font-style: normal;
  border-radius: 0.35rem;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  margin: 0.35rem 0 0 0;
  width: fit-content;
  flex-shrink: 0;
  border: 1px solid gray;
}

/** The tag that contains a verified by string. */
.verified {
  color: forestgreen;
  border-color: rgba(34, 139, 34, 0.5);
}

/** A tag with no verification. */
.unverified {
  color: #8492a6;
  border-color: rgb(132, 146, 166, 0.5);
}

/** A tag for a verified cotenant. */
.cotenant {
  color: rgb(139, 34, 139);
  border-color: rgba(139,34,139, .5);
}

/** AI Match confidence. */
.access-event-card__match-confidence {
  display: flex;
  justify-content: flex-start;
  align-content: center;
  align-items: center;
  border-radius: 50%;
  height: 0.75rem;
  width: 0.75rem;
  z-index: 9;
  position: absolute;
  margin-right: 0.35rem;
  position: relative;
  border: 1px solid;
  cursor: pointer;
}

.access-event-card__match-confidence.green {
  background: forestgreen;
}

.access-event-card__match-confidence.yellow {
  background: yellow;
}

.access-event-card__match-confidence.gray {
  background: gray;
}

.access-event-card__match-confidence.cotenant {
  background:  rgba(139,34,139, .5);
}

/** Tooltip */
.access-event-card__best-match-synopsis {
  opacity: 0;
  max-width: 0;
  white-space: nowrap;
  font-size: 0.75rem;
  color: #fff;
  border-radius: 0.35rem;
  margin: 0;
  z-index: 1;
  transition: opacity 350ms ease, max-width 250ms ease;
}

.access-event-card__tag:hover .access-event-card__best-match-synopsis {
  opacity: 1;
  width: inherit;
  max-width: 200px;
  padding-right: 0.35rem;
}

/** The name of the person who verified. */
.access-event-card__verified-by {
  padding: 0.35rem;
  border-radius: 0.35rem;
  background: #324057;
  color: #eff2f7;
  font-style: normal;
  text-align: center;
  transform: scale(0.9);
}

/** The tag text, including the center border, and some padding. */
.tag-text {
  padding: 0.35rem 0 0.35rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

* ~ .tag-text {
  border-left: 1px solid #8492a6;
  padding-left: 0.35rem;
}

.verified .tag-text,
.verified .access-event-card__best-match-synopsis {
  border-color: rgba(34, 139, 34, 0.5);
  color: rgb(34, 139, 34);
}

.unverified .tag-text,
.unverified .access-event-card__best-match-synopsis {
  border-color: gray;
  color: gray;
}

.cotenant .tag-text,
.cotenant .access-event-card__best-match-synopsis {
  color: rgb(139, 34, 139);
  border-color: rgba(139,34,139, .5);
}
</style>
